import {
  useRef,
  useState,
  forwardRef,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
  type UIEvent,
  type RefObject,
  type ForwardedRef,
} from 'react';
import * as React from 'react';
import { Combobox } from '@headlessui/react';

import clsx from 'clsx';
import { useClickAway, useMedia } from 'react-use';
import { useExperiment } from '@hungryroot/experiments';
import { Clock, MagnifyingGlass, X, XCircle } from '@hungryroot/icons';
import {
  TransparentButton,
  BaseModal,
  Dimension,
  Heading,
} from '@hungryroot/ramen';
import {
  type AlgoliaIndexNames,
  AlgoliaGroceryIndex,
  AlgoliaRecipeIndex,
  SearchResultsList as SearchResultsVirtualList,
} from '@hungryroot/search';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { EditableFavoritableRdpModalFromPairingId } from '../../components/RDP/Modal/EditableFavoritableRdpModalFromPairingId';
import { useCustomerServingSize } from '../../hooks/pricing_config/get/get-customer-pricing-config';
import { type InventoriedAlgoliaItem } from '../../hooks/useSearch';
import { SearchResult } from './components/SearchResult/SearchResult';
import { SearchResultPDP } from './components/SearchResultPDP';
import styles from './Search.module.css';

export interface SearchProps {
  testid?: string;
  query: string;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  productId: number | null;
  isSearchDropdownOpen: boolean;
  type: string;
  setType: (type: AlgoliaIndexNames) => void;
  pairingId: number | null;
  setPairingId: Dispatch<SetStateAction<number | null>>;
  onChange: (selection: string | InventoriedAlgoliaItem) => void;
  onModalClose: () => void;
  onAddClick: () => void;
  onRemoveClick: () => void;
  clearQuery: () => void;
  isLoading: boolean;
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
  onClickAway: () => void;
  onFocus: () => void;
  results: InventoriedAlgoliaItem[];
  removeRecentSearch: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    search: string
  ) => void;
  popularSearches: string[] | undefined;
  recentSearches: string[] | undefined;
  popularRecipes: InventoriedAlgoliaItem[];
  popularGroceries: InventoriedAlgoliaItem[];
  toggleSearchModal: () => void;
  onOnboardingComplete?: () => void;
  className?: string;
}

export function Search(props: SearchProps) {
  const {
    testid,
    query,
    searchTerm,
    setSearchTerm,
    productId,
    isSearchDropdownOpen,
    type,
    setType,
    pairingId,
    setPairingId,
    onChange,
    onModalClose,
    onAddClick,
    onRemoveClick,
    clearQuery,
    isLoading,
    isFetching,
    results,
    hasMore,
    loadMore,
    popularSearches = [],
    removeRecentSearch,
    recentSearches,
    popularRecipes,
    popularGroceries,
    onClickAway,
    onFocus,
    onOnboardingComplete,
    className,
  } = props;

  const isDesktop = useMedia(BreakpointQueryLarge);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const ref = useRef(null);
  const [isDividerShown, setIsDividerShown] = useState(false);
  const customerPlanServingSize = useCustomerServingSize();
  const mhrRedesignVariant = useExperiment(171);
  const discoveryHomeVariant = useExperiment(188);

  if (mhrRedesignVariant === undefined || discoveryHomeVariant === undefined) {
    return null;
  }

  const isMhrRedesignVariant = mhrRedesignVariant > 0;
  const isDiscoveryHomeVariant = discoveryHomeVariant > 0;

  const renderTypes = () => {
    if (query.length > 0) {
      return (
        <div
          className={
            isDividerShown
              ? `${styles.categories} ${styles.divider}`
              : styles.categories
          }
          key="headers"
        >
          <TransparentButton
            onClick={() => setType(AlgoliaRecipeIndex)}
            className={`${styles.category} ${
              type === AlgoliaRecipeIndex
                ? styles['active-header']
                : styles['inactive-header']
            }`}
            ariaLabel="Recipes"
            testid="search-recipes"
          >
            <Heading variant="H6">Recipes</Heading>
          </TransparentButton>
          <TransparentButton
            onClick={() => setType(AlgoliaGroceryIndex)}
            className={`${styles.category} ${
              type === AlgoliaGroceryIndex
                ? styles['active-header']
                : styles['inactive-header']
            }`}
            ariaLabel="Groceries"
            testid="search-groceries"
          >
            <Heading variant="H6">Groceries</Heading>
          </TransparentButton>
        </div>
      );
    }
    return null;
  };

  const renderRecent = () => {
    if (query.length === 0 && recentSearches?.length) {
      return (
        <>
          <Heading variant="H6" testid="recent-searches">
            Recent searches
          </Heading>
          {/* headlessui added a feature to automatically select the first option
          by default when the input is focused with no opt-out functionality
          https://github.com/tailwindlabs/headlessui/pull/1279 the experience we
          want when a using is searching is that no option is selected by
          default and what the user searched in their input is respected,
          therefore, we need to create a "fake" and hidden first option,
          and custom logic to handle this user experience */}
          <>
            <Combobox.Option value=""></Combobox.Option>
            {recentSearches.slice(0, 3).map((recent) => {
              return (
                <Combobox.Option
                  key={recent}
                  value={recent}
                  className={styles['no-margin-padding']}
                >
                  {({ active }) => (
                    <div
                      className={`${styles['recent-popular-result']} ${
                        active ? styles.active : ''
                      }`}
                    >
                      <TransparentButton
                        className={styles['recent-popular-text']}
                        ariaLabel={recent}
                        testid={`recent-search-${recent}`}
                      >
                        <span className={styles['recent-popular-icon']}>
                          <Clock />
                        </span>
                        <span className={styles.grow}>{recent}</span>
                      </TransparentButton>
                      <TransparentButton
                        onClick={(e) => removeRecentSearch(e, recent)}
                        ariaLabel={`Remove ${recent} search`}
                        testid={`recent-search-${recent}-remove`}
                      >
                        <X />
                      </TransparentButton>
                    </div>
                  )}
                </Combobox.Option>
              );
            })}
          </>
        </>
      );
    }

    return null;
  };

  const renderPopular = () => {
    if (query.length === 0 && popularSearches.length > 0) {
      return (
        <>
          <Heading
            variant="H6"
            className={styles.popular}
            testid="popular-searches"
          >
            Popular searches
          </Heading>
          {/* headlessui added a feature to automatically select the first option
          by default when the input is focused with no opt-out functionality
          https://github.com/tailwindlabs/headlessui/pull/1279 the experience we
          want when a using is searching is that no option is selected by
          default and what the user searched in their input is respected,
          therefore, we need to create a "fake" and hidden first option,
          and custom logic to handle this user experience */}
          <>
            <Combobox.Option value=""></Combobox.Option>
            {popularSearches.map((popular) => {
              return (
                <Combobox.Option
                  key={popular}
                  value={popular}
                  className={styles['no-margin-padding']}
                >
                  {({ active }) => (
                    <TransparentButton
                      className={`${styles['recent-popular-result']} ${
                        active ? styles.active : ''
                      }`}
                      ariaLabel={popular}
                      testid={`popular-search-${popular}`}
                    >
                      <span className={styles['recent-popular-icon']}>
                        <MagnifyingGlass />
                      </span>
                      <span className={styles.grow}>{popular}</span>
                    </TransparentButton>
                  )}
                </Combobox.Option>
              );
            })}
          </>
        </>
      );
    }

    return null;
  };

  const renderResults = () => {
    if ((query.length > 0 && results.length > 0) || isLoading || isFetching) {
      return (
        <SearchResultsVirtualList
          onScroll={(e: UIEvent<HTMLElement>) => {
            const target = e.target as HTMLElement;
            setIsDividerShown(target.scrollTop !== 0);
          }}
          onLoadMore={loadMore}
          className={styles['results-virtual']}
          hasMore={hasMore}
          results={isLoading === true ? undefined : results}
          customerServingSize={customerPlanServingSize}
          isFetchingNextPage={isLoading || isFetching}
        />
      );
    }

    return null;
  };

  const renderNoResults = () => {
    const hits =
      type === AlgoliaRecipeIndex ? popularRecipes : popularGroceries;
    if (query.length > 0 && results.length === 0 && !isLoading) {
      return (
        <>
          <div className={styles['no-results']}>No results for "{query}"</div>
          <Heading variant="H5" className={styles['popular-foods']}>
            Check out these popular{' '}
            {type === AlgoliaRecipeIndex ? 'recipes' : 'groceries'}
          </Heading>
          <div className={styles.results} key="results">
            {/* headlessui added a feature to automatically select the first option
          by default when the input is focused with no opt-out functionality
          https://github.com/tailwindlabs/headlessui/pull/1279 the experience we
          want when a using is searching is that no option is selected by
          default and what the user searched in their input is respected,
          therefore, we need to create a "fake" and hidden first option,
          and custom logic to handle this user experience */}
            <>
              <Combobox.Option value=""></Combobox.Option>
              {hits.map((result) => {
                return (
                  <>
                    <Combobox.Option
                      value={result}
                      key={result.item.name}
                      className={styles['no-margin-padding']}
                    >
                      {({ active }) => (
                        <SearchResult
                          result={result.item}
                          status={result.status}
                          dietaryRestrictions={result.dietaryRestrictions}
                          key={result.item.objectID}
                          active={active}
                          isLoading={false}
                        ></SearchResult>
                      )}
                    </Combobox.Option>
                    <hr></hr>
                  </>
                );
              })}
            </>
          </div>
        </>
      );
    }

    return null;
  };

  const renderContent = () => {
    return (
      <Combobox as="div" ref={ref} value={searchTerm} onChange={onChange}>
        <>
          <div className={styles.header}>
            <Combobox.Button
              as={TransparentButton}
              className={clsx({
                [styles.input]: true,
                [styles['active-input']]:
                  isSearchDropdownOpen &&
                  pairingId === null &&
                  productId === null,
                [styles['input-rounded']]:
                  isMhrRedesignVariant || isDiscoveryHomeVariant,
              })}
              testid="search-button"
            >
              <MagnifyingGlass />
              <Combobox.Input
                ref={inputRef}
                className={styles['search-text-input']}
                placeholder="Search for recipes, groceries"
                onChange={(event) => setSearchTerm(event.target.value)}
                autoComplete="off"
                onFocus={() => {
                  onOnboardingComplete && onOnboardingComplete();
                  onFocus();
                }}
                data-testid="search-input"
              />
              {searchTerm.length > 0 && (
                <>
                  <span
                    className={`${styles.desktop} ${styles.clear}`}
                    onClick={() => clearQuery()}
                    data-testid="search-clear"
                  >
                    <Heading variant="H6">Clear</Heading>
                  </span>
                  <span
                    className={`${styles.mobile} ${styles['clear-icon']}`}
                    onClick={() => clearQuery()}
                  >
                    <XCircle />
                  </span>
                </>
              )}
            </Combobox.Button>
            <div className={styles.mobile}>
              <span
                className={styles.clear}
                onClick={onClickAway}
                data-testid="search-cancel"
              >
                <Heading variant="H6">Cancel</Heading>
              </span>
            </div>
          </div>
          {isDesktop && isSearchDropdownOpen && (
            <Dimension
              level="four"
              className={`${styles.content} ${styles.panel} ${
                query.length > 0 ? styles['remove-padding'] : ''
              }`}
            >
              <Combobox.Options static>
                {renderTypes()}
                {renderRecent()}
                {renderPopular()}
                {renderResults()}
                {renderNoResults()}
              </Combobox.Options>
            </Dimension>
          )}
          {!isDesktop && (
            <div className={`${styles.content}`}>
              <Combobox.Options static>
                {renderTypes()}
                {query.length === 0 && (
                  <div className={styles['recent-searches']}>
                    {renderRecent()}
                    {renderPopular()}
                  </div>
                )}
                {renderResults()}
                {renderNoResults()}
              </Combobox.Options>
            </div>
          )}
        </>
        {renderModals()}
      </Combobox>
    );
  };

  const renderModals = () => {
    return (
      <>
        {productId && (
          <SearchResultPDP
            productId={productId}
            onModalClose={onModalClose}
            onAddClick={onAddClick}
            onRemoveClick={onRemoveClick}
          />
        )}
        {pairingId !== null && pairingId > 0 && (
          <EditableFavoritableRdpModalFromPairingId
            pairingId={pairingId}
            onDismiss={() => setPairingId(null)}
          />
        )}
      </>
    );
  };

  if (isDesktop) {
    return (
      <DesktopSearch
        ref={ref}
        onClickAway={onClickAway}
        testid={testid}
        className={className}
      >
        {renderContent()}
      </DesktopSearch>
    );
  }

  return (
    <BaseModal
      testid="search-sheet"
      isOpen={isSearchDropdownOpen}
      ariaLabel="Search"
      onDismiss={onClickAway}
      overlayClassName={styles.underlay}
      contentClassName={styles['modal-content']}
      innerContentClassName={styles['mobile-container']}
      initialFocusRef={inputRef}
    >
      {renderContent()}
    </BaseModal>
  );
}

const DesktopSearch = forwardRef(
  (
    props: {
      children: ReactNode;
      testid: string | undefined;
      onClickAway: () => void;
      className?: string;
    },
    ref: ForwardedRef<HTMLElement>
  ) => {
    const { children, testid, onClickAway, className } = props;
    useClickAway(ref as RefObject<HTMLElement | null>, () => {
      // only close the dropdown when the modal is closed and clicking away
      onClickAway();
    });

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClickAway();
      }
    };

    const handleBlur: React.FocusEventHandler<HTMLDivElement> = (e) => {
      const next = e.relatedTarget as Node | null;
      if (!e.currentTarget.contains(next)) {
        onClickAway();
      }
    };

    return (
      <div
        data-testid={testid}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={clsx(styles.container, className)}
      >
        {children}
      </div>
    );
  }
);
